import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  linkedSignal,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IsDefinedNotEmptyPipe } from '../../pipes/is-defined-not-empty.pipe';
import { outputFromObservable, toObservable } from '@angular/core/rxjs-interop';
import { UiCorePipesModule } from '../../pipes/ui-core-pipes.module';

@Component({
  selector: 'soft-numpad',
  imports: [CommonModule, IsDefinedNotEmptyPipe, UiCorePipesModule],
  templateUrl: './numpad.component.html',
  styleUrl: './numpad.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumpadComponent implements AfterViewInit {
  readonly givenNumbers = [7, 8, 9, 4, 5, 6, 1, 2, 3];

  readonly value = model<number>(0);
  readonly unit = input<string | null>(null);

  readonly floatingPoints = model<number>(2);
  readonly quickNumbers = input<number[]>([]);
  readonly selectExistingInput = model<boolean>(true);
  readonly visibleIncrementDecrement = model<boolean>(true);
  readonly visibleEnter = input<boolean>(true);
  readonly heightPx = input<number>(64);
  readonly min = model<number>();
  readonly max = input<number>();
  readonly disabled = input<boolean>(false);
  private isFirstInput = true;
  readonly decimalDigits = 10 ** this.floatingPoints();

  readonly canSubmit = model<boolean>(true);

  readonly stringValue = linkedSignal<number, string>({
    source: () => this.value(),
    computation: (() => {
      let lastRoundedValidValue = `${Math.round(this.value() * this.decimalDigits) / this.decimalDigits}`;

      return (currentValue) => {
        const min = this.min() ?? Number.MIN_SAFE_INTEGER;
        const max = this.max() ?? Number.MAX_SAFE_INTEGER;

        if (currentValue >= min && currentValue <= max) {
          lastRoundedValidValue = `${Math.round(currentValue * this.decimalDigits) / this.decimalDigits}`;
        }
        return lastRoundedValidValue;
      };
    })(),
  });

  height = computed(() => `${this.heightPx()}px !important`);

  element = viewChild<ElementRef<HTMLInputElement>>('valueElement');

  isOnlyPositive = computed(() => {
    return this.min()! >= 0;
  });

  readonly isNextInputNegative = signal(false);

  readonly numericValue = computed(() => Number(this.stringValue()));

  readonly valueChange = outputFromObservable(toObservable(this.numericValue));
  readonly save = output<number>();

  ngAfterViewInit() {
    if (this.selectExistingInput() && this.stringValue() !== '0') {
      // `focus()` is commented out, because it triggers the virtual keyboard on mobile devices (main use case of this component)
      // this.element()?.nativeElement.focus();
      this.element()?.nativeElement?.select();
    }
  }

  chooseQuickNumber(value: number) {
    this.save.emit(value);
  }

  increment() {
    let value = Number(this.stringValue());
    value++;
    this.stringValue.set(value.toString());
  }

  decrement() {
    let value = Number(this.stringValue());
    value--;
    if (value > this.min()! || value > 0) {
      this.stringValue.set(value.toString());
    }
  }

  appendNumber(num: number) {
    let currentValue = this.stringValue();
    const inputValue = num.toString();

    if (this.selectExistingInput()) {
      if (this.isFirstInput || currentValue === '0') {
        this.stringValue.set(inputValue);
      } else if (this.canAppendNumber(currentValue)) {
        const newValue = currentValue + inputValue;
        if (this.canAppendNumber(newValue)) {
          this.stringValue.set(newValue);
        }
      }
    } else {
      if (this.canAppendNumber(currentValue)) {
        const newValue = currentValue === '0' ? inputValue : currentValue + inputValue;
        if (this.canAppendNumber(newValue)) {
          this.stringValue.set(newValue);
        }
      }
    }
    this.isFirstInput = false;
  }

  appendZeroZero() {
    let value = this.stringValue();
    const decimalPart = value.split('.')[1];
    if (
      Number(value) % 1 !== 0 ||
      value === '0' ||
      decimalPart === '0' ||
      decimalPart === '00'
    )
      return;
    value += '00';

    if (!this.canNotAppendMoreFloatingPoints() && this.canAppendNumber(value)) {
      this.stringValue.set(value);
    } else {
      value = this.stringValue();
      value += '0';
      if (
        !this.canNotAppendMoreFloatingPoints() &&
        this.canAppendNumber(value) &&
        Number(value) !== 0 &&
        !isNaN(Number(value))
      )
        this.stringValue.set(value);
    }
  }

  convertPositiveNegative() {
    if (this.numericValue() !== 0) {
      const newNumericValue = this.numericValue() * -1;
      this.stringValue.set(this.numberToValidDecimalString(newNumericValue));
    } else {
      this.isNextInputNegative.set(true);
    }
  }

  appendComma() {
    let value = this.stringValue();
    const canAppendComma =
      !value.includes('.') && this.floatingPoints() !== 0 && Number(value) !== this.max();
    if (canAppendComma) {
      value += '.';
      this.stringValue.set(value);
    }
  }

  onNumKeyboardInput(event: Event) {
    const inputEvent = event as InputEvent;

    const inputElement = event?.target as HTMLInputElement;
    const input = inputElement.value;
    const normalizedInput = input.replace(',', '.');
    const decimalPart = normalizedInput.split('.')[1];
    const isDeletion = inputEvent.inputType?.startsWith('delete');

    if (this.selectExistingInput() && this.isFirstInput) {
      this.value.set(Number(normalizedInput));
      this.isFirstInput = false;
      return;
    }

    if (
      isDeletion ||
      (this.canAppendNumber(normalizedInput) &&
        !this.canNotAppendMoreFloatingPoints() &&
        !(normalizedInput.split('.').length > 2) &&
        !(this.floatingPoints() === 0 && input.includes(',')) &&
        !(decimalPart && decimalPart.length > this.floatingPoints()))
    ) {
      this.value.set(Number(normalizedInput));
    } else {
      inputElement.value = this.value().toString().replace('.', ',');
      event.preventDefault();
    }
  }

  onEnterKey(event: KeyboardEvent) {
    if (event.key === 'Enter') this.onSave();
  }

  preventSpace(event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    keyboardEvent.preventDefault();
  }

  hasComma(stringValue: string): boolean {
    return stringValue.includes(',') || stringValue.includes('.');
  }

  backspace() {
    this.stringValue.set(this.stringValue().slice(0, this.stringValue().length - 1));
  }

  clear() {
    this.stringValue.set('0');
  }

  onSave() {
    this.save.emit(this.numericValue());
  }

  private numberToValidDecimalString(value: number): string {
    return `${value}`.replace(',', '.');
  }

  private canAppendNumber(input: string) {
    return (
      !this.canNotAppendMoreFloatingPoints() &&
      Number(input) < Number.MAX_SAFE_INTEGER &&
      Number(input) > Number.MIN_SAFE_INTEGER &&
      Number(input) >= (this.min() ?? Number.MIN_SAFE_INTEGER) &&
      Number(input) <= (this.max() ?? Number.MAX_SAFE_INTEGER)
    );
  }

  private canNotAppendMoreFloatingPoints = computed(() => {
    const regex = new RegExp(`^\\d+\\.\\d{${this.floatingPoints()}}$`);
    return regex.test(this.stringValue());
  });
}
